// RITA-1525 (Epic RITA-901) — Probeseite des Vertragseditors: Beispielvertrag auf A4-Seiten mit
// Kopfzeile, Logo, Seitenzahlen, manuellen und automatischen Umbrüchen, PDF-Probe (seit RITA-1542 vom Server), (seit
// Teilpaket 2) direkter Bearbeitung auf den Seiten und (seit Teilpaket 3) der Platzhalterleiste,
// deren Position die Seite im localStorage merkt. Nur für SUPERADMIN (pages.tsx superAdminOnly,
// Routes.tsx RequireSuperAdmin). Speichern gehört zur getrennten VertragsvorlagePage; die Probe
// verwendet denselben Arbeitsbereich, bleibt aber ohne Speicheranbindung.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { PagePathname } from '../../../../routes/paths';
import VertragArbeitsbereich from '../components/VertragArbeitsbereich';
import { useVertragEditor } from '../editor/useVertragEditor';
import { beispielvertrag } from '../fixtures/beispielvertrag';
import { Beispielauswahl, Beispielfall, STANDARD_BEISPIELFALL } from '../model/beispielfaelle';
import useVertragsartenMitLogo from '../../hooks/useVertragsartenMitLogo';
import usePdfProbe from '../../hooks/usePdfProbe';
import VertragLeistenRahmen, { VertragTitelzeile } from '../components/VertragLeistenRahmen';

const RUECKSPRUNG = `${PagePathname.briefvorlagen}?bereich=vertragsvorlagen`;

function VertragseditorProbePage() {
  const navigate = useNavigate();
  const [anfang] = useState(() => beispielvertrag());
  const editor = useVertragEditor(anfang);
  // Vertragsarten mit Logo (RITA-1526) für Werkzeugleiste, Seiten und Druckansicht.
  const vertragsarten = useVertragsartenMitLogo();
  // RITA-1542: „PDF-Probe" holt die Server-PDF des aktuellen Stands mit Beispielwerten.
  const pdfProbe = usePdfProbe();
  // RITA-1597: Beispielfall der Ansicht; die Probeseite hat keine Zuordnung, {{FIRMA}} zeigt die Fallback-Marke.
  const [beispielfall, setBeispielfall] = useState<Beispielfall>(STANDARD_BEISPIELFALL);
  const beispiel = useMemo<Beispielauswahl>(() => ({ fall: beispielfall, marke: null }), [beispielfall]);

  return (
    <VertragLeistenRahmen>
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <Box
              component="span"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate(PagePathname.einstellung)}
            >
              Einstellungen
            </Box>
            {' › '}
            <Box
              component="span"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => navigate(RUECKSPRUNG)}
            >
              Vorlagen
            </Box>
            {' › '}
            <Box component="span" sx={{ color: 'text.primary' }}>
              Vertragseditor (Probe)
            </Box>
          </Typography>
        </Box>

        <VertragTitelzeile
          titel="Vertragseditor: Probeansicht"
          fehler={
            pdfProbe.fehler ? (
              <Alert
                severity="error"
                data-testid="vertragsvorlage-pdf-fehler"
                sx={{ mt: 1 }}
                onClose={pdfProbe.fehlerSchliessen}
              >
                {pdfProbe.fehler}
              </Alert>
            ) : undefined
          }
          aktionen={
            <>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(RUECKSPRUNG)}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Zurück
              </Button>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={() => editor.befehle.zuruecksetzen(beispielvertrag())}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Beispiel neu laden
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfOutlinedIcon />}
                disabled={pdfProbe.laeuft}
                onClick={() => pdfProbe.oeffnen(editor.zustand, 'Beispielvertrag (Probe)', beispiel)}
              >
                {pdfProbe.laeuft ? 'PDF wird erstellt …' : 'PDF-Probe'}
              </Button>
            </>
          }
        />

        <Alert severity="info" sx={{ mb: 2 }}>
          Probeversion: Der Beispielvertrag lässt sich direkt auf den A4-Seiten bearbeiten (Tippen, Enter, Fett/Kursiv/
          Unterstrichen, Schriftgröße, Aufzählung, Kästchen, manueller Seitenumbruch, Kopf- und Fußzeile per Klick oder
          Werkzeugleiste, Rückgängig mit Strg/Cmd+Z). Platzhalter fügt die Platzhalterleiste an der Schreibmarke ein;
          eingefügter Text mit {'{{VORNAME}}'} wird ebenfalls zum Platzhalter. Änderungen werden noch nicht gespeichert;
          „Beispiel neu laden“ verwirft sie. „PDF-Probe“ öffnet die vom Server erzeugte PDF (PDF/A, Beispielwerte) in
          einem neuen Tab; das Logo wird nicht gedruckt.
        </Alert>

        <VertragArbeitsbereich
          editor={editor}
          vertragsarten={vertragsarten}
          beispiel={beispiel}
          onBeispielfallChange={setBeispielfall}
        />
      </Box>
    </VertragLeistenRahmen>
  );
}

export default VertragseditorProbePage;
